// ============================================================
// WHOSE SIDE THE ARRIVALS JOIN (v8.315.0)
// ============================================================
// One field on the flag, set before a pass: import the players under
// "Kill-team", move the flag, import the opposition under "Waaagh". A team
// is a fact on the sheet, so the stamp is an ordinary sheet write - and it
// forbids nothing.

import { useState } from 'react';
import clsx from '../../utils/clsx';
import common from '../../styles/common.module.css';
import styles from '../../styles/Battlefield.module.css';
import { teamTag } from '../../engine/roster/team';
import { mintUnique } from '../../utils/mintUnique';

const NEW_TEAM = '__new';

/**
 * What choosing `value` in the team picker means: 'name' to open the name
 * box, null for no team, a team to stamp - or undefined for a tag that is
 * not among `teams` (nothing to do).
 */
export function teamChoice(value, teams) {
  if (value === NEW_TEAM) return 'name';
  if (value === '') return null;
  const t = teams.find((x) => x.tag === value);
  return t ? { tag: t.tag, label: t.label, ...(t.livery ? { livery: t.livery } : {}) } : undefined;
}

/**
 * The name box's state, held by the bar itself so it outlives the field -
 * the bar drops the field while no marker is planted, and a half-made team
 * must still be there when one is.
 */
export function useTeamNaming() {
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState('');
  return { naming, setNaming, newName, setNewName };
}

/**
 * "New team…" opens a name box; the tag is minted on Add and the livery
 * left unpainted, so the roster hands the new side its colour. Leaving the
 * box empty closes it again.
 */
function NewTeamForm({ onTeam, naming }) {
  const { newName, setNewName, setNaming } = naming;
  const submit = (e) => {
    e.preventDefault();
    const label = newName.trim();
    if (!label) return;
    onTeam({ tag: teamTag(mintUnique()), label });
    setNewName('');
    setNaming(false);
  };
  return (
    <form style={{ display: 'inline-flex', gap: 4 }} onSubmit={submit}>
      <input
        className={clsx(common.input, styles.deployRadius)}
        data-testid="deploy-team-name"
        aria-label="New team name"
        value={newName}
        placeholder="Waaagh"
        onChange={(e) => setNewName(e.target.value)}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the new-team form appears on the user's own click
        autoFocus
        onBlur={() => {
          if (!newName.trim()) setNaming(false);
        }}
      />
      <button type="submit" className={common.btnSmall} data-testid="deploy-team-add" disabled={!newName.trim()}>
        Add
      </button>
    </form>
  );
}

/**
 * The team picker. The marker's own team may not be on the roster yet -
 * nobody has arrived under it - so it is offered too, or the select could
 * not show it.
 */
function TeamSelect({ marker, teams, onTeam, onName }) {
  const onChange = (e) => {
    const choice = teamChoice(e.target.value, teams);
    if (choice === 'name') onName();
    else if (choice !== undefined) onTeam(choice);
  };
  const unlisted = marker.team && !teams.some((t) => t.tag === marker.team.tag);
  return (
    <select
      className={clsx(common.input, styles.deployTeam)}
      data-testid="deploy-team"
      aria-label="Team for arrivals"
      value={marker.team?.tag ?? ''}
      onChange={onChange}
    >
      <option value="">No team</option>
      {teams.map((t) => (
        <option key={t.tag} value={t.tag}>
          {t.label}
        </option>
      ))}
      {unlisted && <option value={marker.team.tag}>{marker.team.label}</option>}
      <option value={NEW_TEAM}>New team…</option>
    </select>
  );
}

/** `naming` is useTeamNaming's state, held by the bar. */
export default function TeamField({ marker, teams, onTeam, naming }) {
  return (
    <label className={common.label}>
      Team
      {naming.naming ? (
        <NewTeamForm onTeam={onTeam} naming={naming} />
      ) : (
        <TeamSelect marker={marker} teams={teams} onTeam={onTeam} onName={() => naming.setNaming(true)} />
      )}
    </label>
  );
}
